import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

export default function MonthNewMembersModal({
  open,
  onClose,
  items = [], // kpis.monthNewMemberItems
  monthFrom,
  monthTo,
  onSelectMember, // (row) => ...
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [q, setQ] = useState('');
  const [sortDir, setSortDir] = useState('asc'); // asc|desc

  const headerSubtitle = useMemo(() => {
    const range = monthFrom && monthTo ? `${monthFrom} → ${monthTo}` : '';
    return range ? `Dönem: ${range}` : 'Bu ay yeni kayıt olan üyeler';
  }, [monthFrom, monthTo]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let arr = Array.isArray(items) ? items : [];

    if (qq) {
      arr = arr.filter((x) =>
        String(x.fullName || '')
          .toLowerCase()
          .includes(qq)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;

    return [...arr].sort((a, b) => {
      const A = String(a.fullName || '');
      const B = String(b.fullName || '');
      return A.localeCompare(B, 'tr') * dir;
    });
  }, [items, q, sortDir]);

  const totalCount = items?.length || 0;
  const shownCount = filtered?.length || 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.15 }}>
              Yeni Üyeler (Ay)
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
              {headerSubtitle}
            </MDTypography>
          </MDBox>

          <IconButton onClick={onClose} size="small" aria-label="close">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Summary */}
        <MDBox
          mb={2}
          p={2}
          borderRadius="lg"
          sx={(t) => ({ border: `1px solid ${t.palette.divider}` })}
        >
          <MDBox
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
            justifyContent="space-between"
            gap={isMobile ? 1.25 : 2}
          >
            <MDBox>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Toplam yeni üye
              </MDTypography>
              <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                {totalCount}
              </MDTypography>
            </MDBox>

            <MDBox textAlign={isMobile ? 'left' : 'right'}>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Görünen
              </MDTypography>
              <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                {shownCount}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Search + Sort */}
        <MDBox
          mb={2}
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          gap={1}
          alignItems={isMobile ? 'stretch' : 'center'}
        >
          <MDInput
            placeholder="Üye ara…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
          />
          {q && (
            <MDButton variant="outlined" color="dark" onClick={() => setQ('')}>
              Temizle
            </MDButton>
          )}

          <MDButton
            variant={sortDir === 'asc' ? 'gradient' : 'outlined'}
            color="dark"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          >
            İsim {sortDir === 'asc' ? '↑' : '↓'}
          </MDButton>
        </MDBox>

        {/* Card list */}
        <MDBox display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} gap={1.25}>
          {filtered.length === 0 ? (
            <MDBox
              p={2}
              borderRadius="lg"
              sx={(t) => ({
                gridColumn: '1 / -1',
                border: `1px solid ${t.palette.divider}`,
              })}
            >
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Kayıt bulunamadı.
              </MDTypography>
            </MDBox>
          ) : (
            filtered.map((x) => (
              <MDBox
                key={x.id}
                p={1.5}
                borderRadius="lg"
                onClick={() => onSelectMember?.(x)}
                sx={(t) => ({
                  border: `1px solid ${t.palette.divider}`,
                  cursor: onSelectMember ? 'pointer' : 'default',
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: t.boxShadows?.md ?? '0 6px 18px rgba(0,0,0,0.08)',
                  },
                })}
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <MDBox sx={{ minWidth: 0 }}>
                    <MDTypography variant="button" fontWeight="medium" sx={{ lineHeight: 1.2 }}>
                      {x.fullName}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                      Üye ID: {x.id}
                    </MDTypography>
                  </MDBox>

                  <Icon sx={{ opacity: 0.6, fontSize: 18 }}>chevron_right</Icon>
                </MDBox>
              </MDBox>
            ))
          )}
        </MDBox>

        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
          Not: Kart’a tıklamayı “üye detayına git” akışına bağlayabiliriz.
        </MDTypography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton variant="gradient" color="dark" onClick={onClose} fullWidth={isMobile}>
          Kapat
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
